/**
 * The summary list: one Firestore document holding every trip's cheap view.
 *
 * ── The read quota, which is the real ceiling ─────────────────────────────
 *
 * Firestore bills per DOCUMENT read. Today five `/v2` screens and `/app` each
 * open their own `onSnapshot` over the whole `trips` collection, so an
 * advisor with 60 trips spends 60 reads per screen per open. At ~8 opens a
 * day that is ~500 reads for ONE advisor — roughly 100 advisors before the
 * 50,000-reads/day free quota is gone, at which point the service stops until
 * midnight Pacific. (HANDOFF §7, "Storage ceilings".)
 *
 * `users/{uid}/meta/tripIndex` is that list in a single document, so drawing
 * it costs ONE read however many trips there are. It is a derived cache and
 * nothing else: every value in it can be recomputed from the trips
 * collection, so if it is missing, stale or wrong the answer is always
 * `rebuildTripIndex`, never a repair.
 *
 * ── It carries the money on purpose ───────────────────────────────────────
 *
 * Dashboard totals, the pipeline board, pending commission and search all
 * scan every trip. If the summary did not carry their numbers they would
 * fetch every trip in full and the index would have bought nothing. See the
 * header of `trip-summary.js` for what that costs in exchange.
 */

import { mergeDoc, readCollection, readDoc, removeField, watchDoc, watchWhere, writeBatched } from './firebase.js'
import { tripIndexPath, tripsPath } from './trip-paths.js'
import { migrateTrip, normalizeTrip, TRIP_SCHEMA_VERSION } from './trip-shape.js'
import { MAX_INDEXED_TRIPS, MAX_INDEX_BYTES, SUMMARY_VERSION, estimateIndexBytes, tripSummary } from './trip-summary.js'

/** Bump when the INDEX document's own shape changes. */
export const TRIP_INDEX_VERSION = 1

/**
 * The merge that keeps one trip's index entry in step with the trip.
 * Returned as an operation rather than performed, so `trips.js` can commit it
 * in the same batch as the trip itself — half of that pair is a trip missing
 * from every list, or a list entry pointing at nothing.
 *
 * @param {string} uid
 * @param {any} trip - the WHOLE trip; a summary carries totals from ~50 fields
 * @returns {{op: 'merge', path: string[], data: any}}
 */
export function indexEntryOp(uid, trip) {
  return {
    op: 'merge',
    path: tripIndexPath(uid),
    data: {
      indexVersion: TRIP_INDEX_VERSION,
      summaryVersion: SUMMARY_VERSION,
      schemaVersion: TRIP_SCHEMA_VERSION,
      updatedAt: String(trip.updatedAt || ''),
      trips: { [trip.id]: tripSummary(trip) },
    },
  }
}

/**
 * The merge that removes one trip's entry.
 * @param {string} uid @param {string|number} id
 * @returns {{op: 'merge', path: string[], data: any}}
 */
export function indexRemoveOp(uid, id) {
  return {
    op: 'merge',
    path: tripIndexPath(uid),
    data: { trips: { [String(id)]: removeField() } },
  }
}

/** @param {any} doc @returns {any[]} the summaries a raw index document holds */
function summariesOf(doc) {
  const byId = (doc && doc.trips) || {}
  return Object.keys(byId).map((id) => ({ ...byId[id], id: byId[id].id || id }))
}

/**
 * Is a stored index still trustworthy?
 *
 * A summary is only as right as the code that built it, so an index written
 * by an older derivation is not merged with or patched — it is rebuilt. This
 * is the check that stops a money fix in `revenue.js` from leaving every
 * dashboard total frozen at the pre-fix reading.
 *
 * @param {any} doc @returns {boolean}
 */
export function indexIsCurrent(doc) {
  if (!doc || typeof doc !== 'object') return false
  if (doc.overflowed === true) return false
  return doc.indexVersion === TRIP_INDEX_VERSION && doc.summaryVersion === SUMMARY_VERSION
}

/**
 * Read the summary list. ONE document read.
 *
 * `missing` and `stale` both mean "call `rebuildTripIndex`". They are told
 * apart so a caller can say "setting up" the first time and nothing at all
 * on a version bump.
 *
 * @param {string} uid
 * @returns {Promise<{status: 'ready'|'missing'|'stale', summaries: any[], doc: any}>}
 */
export async function loadTripIndex(uid) {
  const doc = await readDoc(tripIndexPath(uid))
  if (!doc) return { status: 'missing', summaries: [], doc: null }
  if (!indexIsCurrent(doc)) return { status: 'stale', summaries: summariesOf(doc), doc }
  return { status: 'ready', summaries: summariesOf(doc), doc }
}

/**
 * Watch the summary list. After the first read this costs a read only when a
 * trip actually changes.
 *
 * @param {string} uid
 * @param {(state: {status: 'ready'|'missing'|'stale', summaries: any[], doc: any}) => void} onChange
 * @param {(err: Error) => void} [onError]
 * @returns {() => void}
 */
export function watchTripIndex(uid, onChange, onError) {
  return watchDoc(
    tripIndexPath(uid),
    (doc) => {
      if (!doc) return onChange({ status: 'missing', summaries: [], doc: null })
      onChange({ status: indexIsCurrent(doc) ? 'ready' : 'stale', summaries: summariesOf(doc), doc })
    },
    onError
  )
}

/**
 * Watch for trips `/app` changed behind the index's back.
 *
 * The legacy editor does not know the index exists, so every save there
 * leaves it stale. This is a FILTERED listener — `updatedAt > sinceIso` —
 * which costs a read per changed document and nothing at all when nothing has
 * changed, which is the normal case. Feed what it delivers to
 * `refreshIndexEntries`.
 *
 * Two things it cannot see, both by construction:
 *   - a DELETION in `/app`: there is no document left to match. Those linger
 *     until `forgetTrip` drops one that turns out to be gone, or a rebuild.
 *   - a trip with no `updatedAt` at all. Every trip `saveToFirestore` has
 *     ever written has one (app.html:8191); anything older is caught by the
 *     rebuild that gave the account its index in the first place.
 *
 * @param {string} uid
 * @param {string} sinceIso
 * @param {(trips: any[]) => void} onChange
 * @returns {() => void}
 */
export function watchTripChanges(uid, sinceIso, onChange) {
  return watchWhere(tripsPath(uid), ['updatedAt', '>', String(sinceIso || '')], (docs) =>
    onChange(docs.map((d) => normalizeTrip(d.data, d.id)))
  )
}

/**
 * Fold trips that changed elsewhere back into the index. ONE write.
 * @param {string} uid @param {any[]} trips
 * @returns {Promise<number>} how many entries were written
 */
export async function refreshIndexEntries(uid, trips) {
  const list = (Array.isArray(trips) ? trips : []).filter((t) => t && t.id)
  if (!list.length) return 0
  /** @type {Record<string, any>} */
  const byId = {}
  for (const trip of list) byId[trip.id] = tripSummary(trip)
  await mergeDoc(tripIndexPath(uid), {
    indexVersion: TRIP_INDEX_VERSION,
    summaryVersion: SUMMARY_VERSION,
    trips: byId,
  })
  return list.length
}

/**
 * Drop an index entry for a trip that is no longer there.
 * @param {string} uid @param {string|number} id
 * @returns {Promise<void>}
 */
export function forgetTrip(uid, id) {
  return mergeDoc(tripIndexPath(uid), { trips: { [String(id)]: removeField() } })
}

/**
 * Rebuild the index from the trips collection. N reads, ONE write.
 *
 * This IS the migration. It is how an account that predates the index gets
 * one, how a bumped `summaryVersion` is repaired, and how anything deleted in
 * `/app` stops being listed. Idempotent — running it twice produces the same
 * document, per ARCHITECTURE.md §7.
 *
 * `migrated` names the trips whose stored shape is behind v1. They are NOT
 * written back: that would be N writes, each one a whole-document overwrite
 * racing the advisor's open `/app` tab. The index is built from the migrated
 * reading, so every screen sees the corrected values; the documents catch up
 * the next time each trip is saved.
 *
 * @param {string} uid
 * @param {{now?: () => number}} [options]
 * @returns {Promise<{count: number, overflowed: boolean, bytes: number, migrated: string[]}>}
 */
export async function rebuildTripIndex(uid, options = {}) {
  const clock = options.now || (() => Date.now())
  const raw = await readCollection(tripsPath(uid))

  /** @type {Record<string, any>} */
  const byId = {}
  const migrated = []
  let newest = ''
  for (const doc of raw) {
    const result = migrateTrip(normalizeTrip(doc, doc.id))
    if (result.changed) migrated.push(result.trip.id)
    byId[result.trip.id] = tripSummary(result.trip)
    const stamp = String(result.trip.updatedAt || '')
    if (stamp > newest) newest = stamp
  }

  const count = Object.keys(byId).length
  const bytes = estimateIndexBytes(byId)
  /* Both, because neither alone is enough: 750 trips of ordinary length are
     573 KB and 750 with long destination strings are 744 KB, and a single
     pathological trip can be larger than either average predicts. */
  const overflowed = count > MAX_INDEXED_TRIPS || bytes > MAX_INDEX_BYTES

  await writeBatched([
    {
      op: 'set',
      path: tripIndexPath(uid),
      data: {
        indexVersion: TRIP_INDEX_VERSION,
        summaryVersion: SUMMARY_VERSION,
        schemaVersion: TRIP_SCHEMA_VERSION,
        builtAt: new Date(clock()).toISOString(),
        updatedAt: newest,
        count,
        overflowed,
        bytes,
        /* Above the cap the index would eventually exceed Firestore's 1 MiB
           document limit, and then the WRITE fails — taking the trip save
           batched with it down too. Storing nothing and flagging it is
           recoverable; a list that silently loses trips is not. */
        trips: overflowed ? {} : byId,
      },
    },
  ])

  return { count, overflowed, bytes, migrated }
}
